import { Apple, Check, Download, Smartphone } from "lucide-react";
import QRCode from "react-qr-code";

import { useI18nStore } from "../../stores/i18nStore";
import { useInstallPrompt } from "../../hooks/useInstallPrompt";
import { APK_URL, androidInstallUrl, siteUrl } from "./appInstall";
import { StepList } from "./StepList";

/**
 * "Install in three steps", directly below the hero.
 *
 * One QR code per platform, because the two platforms genuinely go to different
 * places. Android can download and install a signed APK; iOS cannot install
 * anything but a home-screen web app, since Apple permits no sideloading at
 * all. A single shared code would have to point at whichever of those is wrong
 * for half the visitors.
 *
 * Both codes encode the site's own origin rather than a hard-coded address, so
 * a staging deployment produces codes that open staging. They render as inline
 * SVG - no image request, nothing to 404, sharp at any size.
 *
 * The landing page carries a fuller install section near the bottom, with five
 * steps per platform. This one is deliberately not that: it sits where a
 * visitor on a laptop first looks, and its whole job is to get them onto their
 * phone.
 */

function Qr({ value, label }: { value: string; label: string }) {
  return (
    <div className="keep-light rounded-xl border border-gray-200 bg-white p-3">
      {/* Sized in CSS rather than by the value prop, so it stays crisp on a
          high-density screen and still shrinks on a narrow one. */}
      <QRCode
        value={value}
        size={256}
        style={{ height: "auto", maxWidth: "100%", width: "100%" }}
        viewBox="0 0 256 256"
        title={label}
      />
    </div>
  );
}

export function QuickInstallSection() {
  const { t } = useI18nStore();
  const url = siteUrl();
  const { state: installState, install } = useInstallPrompt();

  // The install page, never the .apk itself. A phone's QR scanner hands the
  // link to whatever in-app browser it embeds, and those cannot start a file
  // download - the tap appears to do nothing. A normal page with a Download
  // button opens in Chrome, which can. Without a published APK the code falls
  // back to the site, still installable through Chrome's own install prompt.
  const androidTarget = APK_URL ? androidInstallUrl() : url;
  const androidSteps = APK_URL
    ? [
        t.quickInstallAndroidApkStep1,
        t.quickInstallAndroidApkStep2,
        t.quickInstallAndroidApkStep3,
      ]
    : [
        t.quickInstallAndroidStep1,
        t.quickInstallAndroidStep2,
        t.quickInstallAndroidStep3,
      ];

  const platforms = [
    {
      key: "android",
      icon: Smartphone,
      title: t.quickInstallAndroidTitle,
      target: androidTarget,
      steps: androidSteps,
      note: null as string | null,
    },
    {
      key: "ios",
      icon: Apple,
      title: t.quickInstallIosTitle,
      // Always the site. Apple allows no sideloading, so there is no download
      // to point at and never will be.
      target: url,
      steps: [
        t.quickInstallIosStep1,
        t.quickInstallIosStep2,
        t.quickInstallIosStep3,
      ],
      // Said out loud because it is the most common way this goes wrong: only
      // Safari can add a web app to the iOS home screen, and the other browsers
      // simply have no such menu item rather than failing visibly.
      note: t.quickInstallIosNote,
    },
  ];

  return (
    <section
      className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-200"
      data-testid="quick-install-section"
      aria-labelledby="quick-install-heading"
    >
      <div className="max-w-5xl mx-auto">
        <div className="text-center mb-10">
          <h2
            id="quick-install-heading"
            className="text-3xl sm:text-4xl font-bold text-gray-900 mb-3"
          >
            {t.quickInstallTitle}
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            {t.quickInstallSubtitle}
          </p>

          {/* The real install, when Chrome offers it - the only route to an app
              rather than a bookmark, and it saves hunting through the browser
              menu. Absent on iOS and on any browser that has not judged the
              site installable: a button that silently did nothing would be
              worse than none. */}
          {installState === "available" && (
            <button
              type="button"
              onClick={() => void install()}
              data-testid="install-app-button"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-700"
            >
              <Download aria-hidden="true" className="h-4 w-4" />
              {t.quickInstallButton}
            </button>
          )}
          {installState === "installed" && (
            <p
              data-testid="install-app-installed"
              className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-green-700"
            >
              <Check aria-hidden="true" className="h-4 w-4" />
              {t.quickInstallInstalled}
            </p>
          )}
        </div>

        <div className="grid sm:grid-cols-2 gap-6">
          {platforms.map((platform) => (
            <div
              key={platform.key}
              data-testid={`quick-install-${platform.key}`}
              className="rounded-2xl border border-gray-200 bg-gray-50 p-6"
            >
              <h3 className="flex items-center gap-2 text-base font-semibold text-gray-900 mb-4">
                <platform.icon
                  aria-hidden="true"
                  className="h-5 w-5 text-blue-600"
                />
                {platform.title}
              </h3>

              <div className="flex gap-5">
                <div className="w-32 shrink-0">
                  <Qr
                    value={platform.target}
                    label={`${platform.title} - ${t.quickInstallScanHint}`}
                  />
                  {/* Readable fallback: a printed page, a screenshot, or
                      anyone already holding the phone and unable to scan
                      their own screen. */}
                  <p
                    className="mt-2 text-xs text-gray-500 break-all"
                    data-testid={`quick-install-${platform.key}-target`}
                  >
                    {platform.target.replace(/^https?:\/\//, "")}
                  </p>
                </div>
                <div className="min-w-0 flex-1">
                  <StepList steps={platform.steps} />
                </div>
              </div>

              {platform.note && (
                <p className="mt-4 text-xs leading-5 text-gray-500">
                  {platform.note}
                </p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default QuickInstallSection;
